import { Search } from "lucide-react-native";
import React, { useCallback, useRef } from "react";

import ManuallyAddPlayerOption from "@/components/molecules/ManuallyAddPlayerOption";
import PlayerItem from "@/components/organisms/PlayerItem";
import PlayerListView from "./PlayerListView";

import { ListRenderItem } from "@/components/atoms/FlashList";
import { Icon } from "@/components/atoms/Icon";
import { Spinner } from "@/components/atoms/Spinner";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";
import { WatchlistWatchlistMetaFragment } from "@/generated/graphql";
import { PlayerCardData } from "@/types/playerCardTypes";
import useMobileScreen from "@/hooks/useMobileScreen";
import { useTheme } from "@/providers/ThemeProvider";

/**
 * Props for PlayerListContainer component
 */
interface IPlayerList {
  loading: boolean;
  refreshing: boolean;
  players: PlayerCardData[];
  heightRecommendation?: number;
  onLoadMore: () => void;
  onRefresh: () => void;
  activeWatchlist?: WatchlistWatchlistMetaFragment | null;
  /** Active search query - when provided, empty state shows search-specific UI with manual add option */
  searchQuery?: string;
  /** Callback for manually adding a player when search yields no results */
  onManualAddPlayer?: () => void;
  /**
   * When true, `onLoadMore` is suppressed until the user has actually
   * scrolled the list at least once. FlashList fires `onEndReached` during
   * initial layout when the content underflows the viewport, which otherwise
   * eagerly paginates (pages 2, 3, …) on mount — sequential round-trips that
   * blow SE-5038's initial-render budget. Off by default to preserve existing
   * behavior for every other consumer; the player-detail roster sidebar opts
   * in. Genuine infinite-scroll is preserved: the first real scroll arms it.
   */
  deferLoadMoreUntilScroll?: boolean;
}

/**
 * Container component for player list with loading states and pagination
 * @param root0 - Component props
 * @param root0.loading - Whether initial data is loading
 * @param root0.refreshing - Whether list is being refreshed via pull-to-refresh
 * @param root0.players - Array of player objects to display in the list
 * @param root0.heightRecommendation - Recommended height for list container
 * @param root0.onLoadMore - Callback invoked when scrolling near end to load more players
 * @param root0.onRefresh - Callback invoked when user pulls to refresh the list
 * @param root0.activeWatchlist - Currently selected watchlist for player context
 * @param root0.searchQuery - Active search query; when set alongside onManualAddPlayer, shows ManuallyAddPlayerOption at list footer (inline variant) and in empty state
 * @param root0.onManualAddPlayer - Callback for manually adding a player; when provided with results, renders inline option at list footer; when no results, renders in empty state
 * @param root0.deferLoadMoreUntilScroll - When true, suppresses onLoadMore until the first user scroll, preventing eager pagination from FlashList's initial-layout onEndReached
 */
const PlayerListContainer = ({
  loading,
  refreshing,
  players,
  heightRecommendation,
  onLoadMore,
  onRefresh,
  activeWatchlist,
  searchQuery,
  onManualAddPlayer,
  deferLoadMoreUntilScroll = false,
}: IPlayerList) => {
  const isSmallScreen = useMobileScreen();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  // Tracks whether the user has scrolled the list at least once. Used to
  // suppress the `onEndReached` FlashList fires during initial layout
  // (content underflow) so we don't eagerly paginate on mount. A ref — not
  // state — because flipping it must not trigger a re-render.
  const hasScrolledRef = useRef(false);

  const handleScroll = useCallback(() => {
    hasScrolledRef.current = true;
  }, []);

  // ACCEPTED EDGE CASE (SE-5038): when deferLoadMoreUntilScroll is on and the
  // first page underflows the viewport (no scroll possible), pages 2+ can't be
  // reached. This is negligible in practice: the roster page size is 20, while
  // the 1/4-width sidebar (height ≈ screenHeight − 200) shows only ~9–15 rows
  // on normal desktops. A next page only exists when >20 players are loaded,
  // and 20 rows always overflow the viewport — so whenever pagination is
  // actually needed the list is scrollable. Underflow + hasNextPage could only
  // coincide on a ~1480px-tall viewport (room for all 20 rows at once), an
  // extreme outlier. If a smaller page size is ever introduced here, revisit
  // this and arm pagination on post-load underflow instead.
  const handleLoadMore = useCallback(() => {
    if (deferLoadMoreUntilScroll && !hasScrolledRef.current) {
      return;
    }
    onLoadMore();
  }, [deferLoadMoreUntilScroll, onLoadMore]);

  const renderItem: ListRenderItem<PlayerCardData> = useCallback(
    ({ item }) => (
      <PlayerItem isActive={false} item={item} watchlist={activeWatchlist} />
    ),
    [activeWatchlist]
  );

  const renderListFooterComponent = useCallback(
    () => (
      <>
        {loading && !refreshing && players.length ? (
          <Spinner size="large" color="white" />
        ) : null}
        {!loading && onManualAddPlayer && players.length > 0 ? (
          <ManuallyAddPlayerOption
            subtitle="Can't find who you're looking for?"
            onPress={onManualAddPlayer}
            isDark={isDark}
            variant="inline"
          />
        ) : null}
      </>
    ),
    [loading, refreshing, players.length, onManualAddPlayer, isDark]
  );

  const renderListEmptyComponent = useCallback(
    () => (
      <Stack className="items-center gap-4 pt-8">
        <Icon as={Search} size="xl" className="text-content-muted" />
        <Text
          variant="unstyled"
          className="text-center text-title-sm text-content-muted"
        >
          No players found for &quot;{searchQuery}&quot;
        </Text>
        {onManualAddPlayer ? (
          <ManuallyAddPlayerOption
            subtitle={`Add "${searchQuery}" manually`}
            onPress={onManualAddPlayer}
            isDark={isDark}
          />
        ) : null}
      </Stack>
    ),
    [onManualAddPlayer, isDark, searchQuery]
  );

  const isSearchActive = !!searchQuery && !!onManualAddPlayer;

  return (
    <PlayerListView
      players={players}
      renderItem={renderItem}
      loading={loading}
      refreshing={refreshing}
      onLoadMore={handleLoadMore}
      onScroll={handleScroll}
      onRefresh={onRefresh}
      renderListFooterComponent={renderListFooterComponent}
      renderListEmptyComponent={
        isSearchActive ? renderListEmptyComponent : undefined
      }
      heightRecommendation={heightRecommendation}
      isSmallScreen={isSmallScreen}
    />
  );
};

export default PlayerListContainer;
